import { useCallback, useEffect, useRef, useState } from "react";

import {
  getSponsoredFeedCandidates,
  recordSponsoredClick as sendSponsoredClick,
  recordSponsoredImpression as sendSponsoredImpression,
} from "../lib/sponsoredApi";
import type { SponsoredFeedItem } from "../types/sponsored";

export function useSponsoredFeed(
  userUid: string | undefined,
  enabled: boolean,
  subject?: string
) {
  const [items, setItems] = useState<SponsoredFeedItem[]>([]);
  const impressionDeliveries = useRef(new Set<string>());

  useEffect(() => {
    let active = true;
    impressionDeliveries.current.clear();

    if (!userUid || !enabled) {
      setItems([]);
      return () => {
        active = false;
      };
    }

    getSponsoredFeedCandidates(subject ? { subject } : {})
      .then((nextItems) => {
        if (active) setItems(nextItems);
      })
      .catch((error: unknown) => {
        if (__DEV__) {
          const code =
            typeof error === "object" && error && "code" in error
              ? String(error.code)
              : "unknown";
          console.warn("[sponsored] candidates unavailable", { code });
        }
        if (active) setItems([]);
      });

    return () => {
      active = false;
    };
  }, [enabled, subject, userUid]);

  const recordImpression = useCallback((item: SponsoredFeedItem) => {
    if (impressionDeliveries.current.has(item.deliveryId)) return;
    impressionDeliveries.current.add(item.deliveryId);
    void sendSponsoredImpression(item).catch((error: unknown) => {
      impressionDeliveries.current.delete(item.deliveryId);
      if (__DEV__) console.warn("[sponsored] impression tracking failed", error);
    });
  }, []);

  const recordClick = useCallback((item: SponsoredFeedItem) => {
    void sendSponsoredClick(item).catch((error: unknown) => {
      if (__DEV__) console.warn("[sponsored] click tracking failed", error);
    });
  }, []);

  return { items, recordImpression, recordClick };
}
